import React, { useEffect, useState } from "react";
import api from "../api";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Loader2, ScanLine, Users, Plus, Flag, LogOut, CheckCircle2, MapPin, Lock, Trophy, RefreshCw, Inbox } from "lucide-react";
import { toast } from "../hooks/use-toast";
import { formatSeat } from "../components/SeatMap";
import EnquiryThread, { StatusBadge } from "../components/EnquiryThread";

const fmtDate = (iso) => { try { return new Date(iso).toLocaleString("en-GB", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }); } catch { return iso; } };

export default function VenueDashboard() {
  const { user, loading, logout } = useAuth();
  const nav = useNavigate();
  const [events, setEvents] = useState(null);
  const [venue, setVenue] = useState(null);
  const [open, setOpen] = useState(null); // event id expanded
  const [mode, setMode] = useState({}); // eventId -> 'checkin' | 'sell' | 'guests'
  const [code, setCode] = useState("");
  const [scanResult, setScanResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [guests, setGuests] = useState([]);
  const [sale, setSale] = useState({ tier_id: "", name: "", quantity: 1, pay_method: "cash" });
  const [view, setView] = useState("events"); // events | staff | leaderboard
  const [staff, setStaff] = useState([]);
  const [report, setReport] = useState([]);
  const [newStaff, setNewStaff] = useState({ name: "", email: "", password: "" });
  const [lbEvent, setLbEvent] = useState(""); // product_id filter for leaderboard
  const [lbUpdated, setLbUpdated] = useState(null);
  const [enquiries, setEnquiries] = useState(null);
  const [openEnq, setOpenEnq] = useState(null);
  const [venueEvents, setVenueEvents] = useState([]);
  const loadEnquiries = () => { api.get("/venue/event-options").then(({ data }) => setVenueEvents(data || [])).catch(() => {}); return api.get("/venue/enquiries").then(({ data }) => { setEnquiries(data); setOpenEnq((o) => o ? data.find((e) => e.id === o.id) || null : null); }).catch(() => setEnquiries([])); };
  const enqSend = async (id, body) => { await api.post(`/venue/enquiries/${id}/message`, { body }); await loadEnquiries(); };
  const enqStatus = async (id, s) => { await api.post(`/venue/enquiries/${id}/status`, { status: s }); await loadEnquiries(); toast({ title: `Marked ${s.replace("_", " ")}` }); };
  const enqBook = async (id, eventId) => { await api.post(`/venue/enquiries/${id}/book`, { event_id: eventId }); await loadEnquiries(); toast({ title: eventId ? "Booked to event" : "Booking unlinked" }); };

  const loadStaff = () => {
    api.get("/venue/staff").then(({ data }) => setStaff(data || [])).catch(() => {});
  };
  const loadReport = (pid = lbEvent) => {
    api.get(`/venue/staff-report${pid ? `?product_id=${pid}` : ""}`).then(({ data }) => { setReport(data || []); setLbUpdated(new Date()); }).catch(() => {});
  };
  const addStaff = async (e) => {
    e.preventDefault();
    try {
      await api.post("/venue/staff", { name: newStaff.name.trim(), email: newStaff.email.trim(), password: newStaff.password });
      setNewStaff({ name: "", email: "", password: "" });
      loadStaff(); loadReport();
      toast({ title: "Door-staff login added" });
    } catch (err) { toast({ title: "Error", description: err?.response?.data?.detail || "Could not add", variant: "destructive" }); }
  };
  const removeStaff = async (uid) => {
    if (!window.confirm("Remove this door-staff login?")) return;
    try { await api.delete(`/venue/staff/${uid}`); loadStaff(); loadReport(); toast({ title: "Login removed" }); }
    catch (err) { toast({ title: "Error", description: err?.response?.data?.detail || "Could not remove", variant: "destructive" }); }
  };
  const resetStaff = async (uid, email) => {
    const password = window.prompt(`New password for ${email} (min 6 chars):`);
    if (!password) return;
    try { await api.post(`/venue/staff/${uid}/reset`, { password }); toast({ title: "Password reset" }); }
    catch (err) { toast({ title: "Error", description: err?.response?.data?.detail || "Could not reset", variant: "destructive" }); }
  };

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/login"); return; }
    if (user.role !== "venue") { nav("/"); return; }
    api.get("/venue/me").then(({ data }) => setVenue(data)).catch(() => {});
    api.get("/venue/events").then(({ data }) => setEvents(data || [])).catch(() => setEvents([]));
  }, [user, loading, nav]);

  const editVenueDetails = async () => {
    const address = window.prompt("Venue address:", venue?.address || "");
    if (address === null) return;
    const capStr = window.prompt("Venue capacity (number):", venue?.capacity || "");
    if (capStr === null) return;
    const image = window.prompt("Venue photo URL (shown on your public page):", venue?.image_url || "");
    if (image === null) return;
    try {
      const { data } = await api.put("/venue/me", { address: address.trim(), capacity: parseInt(capStr) || 0, image_url: image.trim() });
      setVenue(data);
      toast({ title: "Venue details updated" });
    } catch (e) { toast({ title: "Error", description: e?.response?.data?.detail || "Could not update", variant: "destructive" }); }
  };


  // Live leaderboard auto-refresh every 20s while viewing staff/leaderboard
  useEffect(() => {
    if (view !== "leaderboard" && view !== "staff") return;
    const id = setInterval(() => loadReport(), 20000);
    return () => clearInterval(id);
  }, [view, lbEvent]);

  const refresh = () => api.get("/venue/events").then(({ data }) => setEvents(data || [])).catch(() => {});

  const checkin = async (e) => {
    e.preventDefault();
    if (!code.trim() || !open) return;
    setBusy(true); setScanResult(null);
    try {
      const { data } = await api.post("/venue/checkin", { code: code.trim() });
      setScanResult(data);
      setCode("");
      refresh();
    } catch (err) {
      setScanResult({ status: "error", message: err?.response?.data?.detail || "Check-in failed" });
    } finally { setBusy(false); }
  };

  const loadGuests = async (ev) => {
    setMode({ ...mode, [ev.id]: "guests" });
    try { const { data } = await api.get(`/venue/events/${ev.id}/tickets`); setGuests(data || []); }
    catch { setGuests([]); }
  };

  const doSale = async (ev, e) => {
    e.preventDefault();
    if (!sale.tier_id) { toast({ title: "Pick a ticket type", variant: "destructive" }); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/venue/events/${ev.id}/door-sale`, {
        product_id: ev.id, tier_id: sale.tier_id,
        entries: sale.name ? [{ name: sale.name, email: "" }] : [],
        quantity: sale.name ? 0 : Math.max(1, parseInt(sale.quantity) || 1),
        paid: true, pay_method: sale.pay_method, origin: window.location.origin,
      });
      toast({ title: "Sold on the door", description: `${data.count} ticket(s) issued (${sale.pay_method}).` });
      setSale({ tier_id: "", name: "", quantity: 1, pay_method: "cash" });
      refresh();
    } catch (err) {
      toast({ title: "Sale failed", description: err?.response?.data?.detail || "Try again", variant: "destructive" });
    } finally { setBusy(false); }
  };

  const flagEvent = async (ev) => {
    if (!window.confirm(`Flag "${ev.title}" to TicketsPro as suspicious? An admin will review it.`)) return;
    try { await api.post(`/venue/events/${ev.id}/flag`); toast({ title: "Event flagged", description: "TicketsPro admins have been notified." }); }
    catch { toast({ title: "Error", description: "Could not flag event", variant: "destructive" }); }
  };

  if (!events) return <div className="min-h-screen bg-[#0d1626] flex items-center justify-center"><Loader2 className="animate-spin text-[#2ec9c9]" size={32} /></div>;

  return (
    <div className="min-h-screen bg-[#0d1626] text-white">
      <div className="border-b border-white/10">
        <div className="max-w-3xl mx-auto px-5 py-4 flex items-center justify-between">
          <div>
            <p className="text-xs text-[#2ec9c9] uppercase tracking-wide flex items-center gap-1.5"><MapPin size={12} /> Venue door</p>
            <h1 className="text-xl font-bold" data-testid="venue-name">{venue?.name || "Venue"}</h1>
            {venue && <p className="text-xs text-white/50">{venue.city}{venue.address ? ` · ${venue.address}` : ""}{venue.capacity ? ` · cap ${venue.capacity}` : ""} <button onClick={editVenueDetails} data-testid="venue-edit-details" className="text-[#2ec9c9] hover:underline ml-1">edit</button></p>}
          </div>
          <button onClick={() => { logout(); nav("/login"); }} data-testid="venue-logout" className="text-white/70 hover:text-white flex items-center gap-1.5 text-sm"><LogOut size={16} /> Sign out</button>
        </div>
        <div className="max-w-3xl mx-auto px-5 flex gap-1 -mb-px">
          {[["events", "Events", MapPin], ["staff", "Door staff", Users], ["leaderboard", "Leaderboard", Trophy], ["enquiries", "Enquiries", Inbox]].map(([v, label, Icon]) => (
            <button key={v} data-testid={`venue-view-${v}`}
              onClick={() => { setView(v); if (v === "staff") { loadStaff(); loadReport(); } if (v === "leaderboard") loadReport(); if (v === "enquiries") loadEnquiries(); }}
              className={`px-4 py-2.5 text-sm font-medium flex items-center gap-1.5 border-b-2 transition-colors ${view === v ? "border-[#2ec9c9] text-white" : "border-transparent text-white/50 hover:text-white/80"}`}>
              <Icon size={14} /> {label}
            </button>
          ))}
        </div>
      </div>

      {view === "enquiries" && (
        <div className="max-w-3xl mx-auto px-5 py-6" data-testid="venue-enquiries-view">
          {!enquiries ? <Loader2 className="animate-spin text-[#2ec9c9]" size={26} /> : enquiries.length === 0 ? (
            <div className="rounded-2xl bg-white/[0.04] border border-white/10 p-8 text-center text-white/50" data-testid="venue-enquiries-empty">No booking enquiries yet. Artists can reach out to your venue and you'll see them here.</div>
          ) : (
            <div className="grid md:grid-cols-[260px_1fr] gap-4">
              <div className="space-y-2">
                {enquiries.map((e) => (
                  <button key={e.id} onClick={() => setOpenEnq(e)} data-testid={`venue-enquiry-${e.id}`}
                    className={`w-full text-left rounded-xl p-3 border transition-colors ${openEnq?.id === e.id ? "border-[#2ec9c9] bg-white/[0.06]" : "border-white/10 bg-white/[0.03] hover:border-white/20"}`}>
                    <div className="flex items-center justify-between gap-2"><span className="font-medium text-white text-sm truncate">{e.artist_name}</span><StatusBadge status={e.status} /></div>
                    <p className="text-xs text-white/50 truncate mt-0.5">{e.subject}</p>
                  </button>
                ))}
              </div>
              <div className="bg-white rounded-2xl p-5">
                {openEnq ? <EnquiryThread enquiry={openEnq} role="venue" onSend={enqSend} onStatus={enqStatus} events={venueEvents} onBook={enqBook} /> : <p className="text-[#8a97a8] text-center py-10">Pick an enquiry to view the conversation.</p>}
              </div>
            </div>
          )}
        </div>
      )}

      {view === "leaderboard" && (
        <div className="max-w-3xl mx-auto px-5 py-6 space-y-4" data-testid="venue-leaderboard-view">
          <div className="rounded-2xl bg-white/[0.04] border border-white/10 p-4">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
              <h2 className="text-sm text-white/60 uppercase tracking-wide flex items-center gap-2">
                <Trophy size={15} className="text-[#f59e0b]" /> Door-staff leaderboard
                <span className="flex items-center gap-1 text-[10px] text-green-400 normal-case tracking-normal"><span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" /> live</span>
              </h2>
              <div className="flex items-center gap-2">
                <select value={lbEvent} onChange={(e) => { setLbEvent(e.target.value); loadReport(e.target.value); }} data-testid="leaderboard-event-filter"
                  className="bg-white/[0.06] border border-white/10 rounded-lg px-3 py-1.5 text-sm text-white outline-none focus:border-[#2ec9c9]">
                  <option value="">All events</option>
                  {events.map((ev) => <option key={ev.id} value={ev.id} className="text-[#16243b]">{ev.title}</option>)}
                </select>
                <button onClick={() => loadReport()} data-testid="leaderboard-refresh" className="text-white/60 hover:text-white p-1.5"><RefreshCw size={15} /></button>
              </div>
            </div>
            {(() => {
              const ranked = [...report].map((s) => ({ ...s, score: (s.sold_count || 0) + (s.checked_in || 0) })).sort((a, b) => b.score - a.score);
              const medals = ["#f59e0b", "#c0c5cf", "#cd7f32"];
              if (ranked.every((s) => s.score === 0)) return <p className="text-white/50 text-sm py-6 text-center">No door activity yet. Sales and check-ins will appear here live on the night.</p>;
              return (
                <div className="space-y-2">
                  {ranked.map((s, i) => (
                    <div key={s.id} data-testid={`leaderboard-row-${s.id}`}
                      className={`flex items-center gap-3 rounded-xl px-3 py-3 ${i < 3 && s.score > 0 ? "bg-white/[0.06]" : "bg-white/[0.02]"}`}>
                      <div className="w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm shrink-0"
                        style={{ backgroundColor: i < 3 && s.score > 0 ? medals[i] : "rgba(255,255,255,0.08)", color: i < 3 && s.score > 0 ? "#16243b" : "#fff" }}>
                        {i + 1}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-sm truncate">{s.name} {s.is_self && <span className="text-[#2ec9c9] text-xs">(you)</span>}</p>
                        <p className="text-xs text-white/50">{s.sold_count} sold · £{Number(s.sold_revenue).toFixed(2)} · {s.checked_in} scanned</p>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-lg font-bold text-[#2ec9c9]">{s.score}</p>
                        <p className="text-[10px] text-white/40 uppercase">actions</p>
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}
            {lbUpdated && <p className="text-[11px] text-white/30 mt-3 text-right">Updated {lbUpdated.toLocaleTimeString("en-GB")} · auto-refreshes every 20s</p>}
          </div>
        </div>
      )}

      {view === "staff" && (
        <div className="max-w-3xl mx-auto px-5 py-6 space-y-4" data-testid="venue-staff-view">
          <div className="rounded-2xl bg-white/[0.04] border border-white/10 p-4">
            <h2 className="text-sm text-white/60 uppercase tracking-wide mb-3">Your door team</h2>
            <div className="space-y-1.5 mb-4">
              {report.length === 0 && <p className="text-white/50 text-sm">No door-staff logins yet. Add one below.</p>}
              {report.map((s) => (
                <div key={s.id} data-testid={`staff-report-${s.id}`} className="flex items-center justify-between bg-white/[0.03] rounded-lg px-3 py-2.5">
                  <div className="min-w-0">
                    <p className="font-medium text-sm truncate">{s.name} {s.is_self && <span className="text-[#2ec9c9] text-xs">(you)</span>}</p>
                    <p className="text-xs text-white/50 truncate">{s.email}</p>
                  </div>
                  <div className="flex items-center gap-4 shrink-0">
                    <div className="text-right text-xs">
                      <p className="text-white/80"><span className="font-semibold text-[#2ec9c9]">{s.sold_count}</span> sold · £{Number(s.sold_revenue).toFixed(2)}</p>
                      <p className="text-white/50">{s.checked_in} checked in</p>
                    </div>
                    {!s.is_self && (
                      <div className="flex items-center gap-2">
                        <button onClick={() => resetStaff(s.id, s.email)} data-testid={`venue-staff-reset-${s.id}`} className="text-xs text-[#2ec9c9] hover:underline">Reset</button>
                        <button onClick={() => removeStaff(s.id)} data-testid={`venue-staff-remove-${s.id}`} className="text-white/70 hover:text-red-400 text-xs">Remove</button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <form onSubmit={addStaff} className="border-t border-white/10 pt-4 grid sm:grid-cols-2 gap-2" data-testid="venue-add-staff-form">
              <input value={newStaff.name} onChange={(e) => setNewStaff({ ...newStaff, name: e.target.value })} data-testid="venue-staff-name" required
                placeholder="Staff name" className="bg-white/[0.06] border border-white/10 rounded-xl px-4 py-2.5 text-white placeholder-white/40 outline-none focus:border-[#2ec9c9]" />
              <input value={newStaff.email} onChange={(e) => setNewStaff({ ...newStaff, email: e.target.value })} data-testid="venue-staff-email" required
                placeholder="Login email" className="bg-white/[0.06] border border-white/10 rounded-xl px-4 py-2.5 text-white placeholder-white/40 outline-none focus:border-[#2ec9c9]" />
              <input type="password" value={newStaff.password} onChange={(e) => setNewStaff({ ...newStaff, password: e.target.value })} data-testid="venue-staff-password" required
                placeholder="Temp password (min 6)" className="bg-white/[0.06] border border-white/10 rounded-xl px-4 py-2.5 text-white placeholder-white/40 outline-none focus:border-[#2ec9c9]" />
              <button disabled={busy} data-testid="venue-staff-add-btn" className="gp-btn-teal rounded-xl px-5 py-2.5 text-sm font-medium disabled:opacity-60">Add staff login</button>
            </form>
            <p className="text-xs text-white/40 mt-3">Each staff member signs in with their own email so door sales and scans are logged under their name.</p>
          </div>
        </div>
      )}

      {view === "events" && (
      <div className="max-w-3xl mx-auto px-5 py-6 space-y-4">
        <h2 className="text-sm text-white/60 uppercase tracking-wide">Events at your venue</h2>
        {events.length === 0 && <p className="text-white/60 text-sm">No upcoming events listed at this venue yet.</p>}
        {events.map((ev) => (
          <div key={ev.id} className="rounded-2xl bg-white/[0.04] border border-white/10 overflow-hidden" data-testid={`venue-event-${ev.id}`}>
            <div className="p-4 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <h3 className="font-semibold truncate">{ev.title}</h3>
                <p className="text-xs text-white/60 mt-0.5">{fmtDate(ev.event_date)} · {ev.business_name}</p>
                <p className="text-xs text-white/50 mt-1">{ev.checked_in}/{ev.sold} checked in</p>
              </div>
              <button onClick={() => flagEvent(ev)} data-testid={`flag-${ev.id}`} title="Report to TicketsPro" className="text-white/40 hover:text-amber-400 shrink-0"><Flag size={16} /></button>
            </div>
            {ev.allowed ? (
              <div className="border-t border-white/10">
                <div className="flex">
                  {[["checkin", "Check in", ScanLine], ["sell", "Sell on door", Plus], ["guests", "Guest list", Users]].map(([m, label, Icon]) => (
                    <button key={m} data-testid={`${m}-tab-${ev.id}`}
                      onClick={() => { setOpen(ev.id); setScanResult(null); if (m === "guests") loadGuests(ev); else setMode({ ...mode, [ev.id]: m }); }}
                      className={`flex-1 py-2.5 text-xs font-medium flex items-center justify-center gap-1.5 transition-colors ${open === ev.id && mode[ev.id] === m ? "bg-[#2ec9c9] text-[#0d1626]" : "text-white/70 hover:bg-white/5"}`}>
                      <Icon size={14} /> {label}
                    </button>
                  ))}
                </div>
                {open === ev.id && mode[ev.id] === "checkin" && (
                  <div className="p-4 border-t border-white/10">
                    <form onSubmit={checkin} className="flex gap-2">
                      <input autoFocus value={code} onChange={(e) => setCode(e.target.value)} data-testid={`checkin-code-${ev.id}`}
                        placeholder="Enter ticket code" className="flex-1 bg-white/[0.06] border border-white/10 rounded-xl px-4 py-2.5 text-white placeholder-white/40 outline-none focus:border-[#2ec9c9]" />
                      <button disabled={busy} data-testid={`checkin-submit-${ev.id}`} className="gp-btn-teal rounded-xl px-5 py-2.5 text-sm font-medium disabled:opacity-60">Check in</button>
                    </form>
                    {scanResult && (
                      <div data-testid="checkin-result" className={`mt-3 rounded-xl p-3 text-sm ${scanResult.status === "ok" ? "bg-green-500/15 text-green-300" : scanResult.status === "already" ? "bg-amber-500/15 text-amber-300" : "bg-red-500/15 text-red-300"}`}>
                        {scanResult.status === "ok" && <span><CheckCircle2 size={14} className="inline mr-1" /> Checked in: {scanResult.attendee_name} ({scanResult.tier_name}{scanResult.seat ? ` · ${formatSeat(scanResult.seat)}` : ""})</span>}
                        {scanResult.status === "already" && <span>Already checked in: {scanResult.attendee_name}</span>}
                        {scanResult.status === "error" && <span>{scanResult.message}</span>}
                      </div>
                    )}
                  </div>
                )}
                {open === ev.id && mode[ev.id] === "sell" && (
                  <div className="p-4 border-t border-white/10">
                    <form onSubmit={(e) => doSale(ev, e)} className="space-y-3">
                      <select value={sale.tier_id} onChange={(e) => setSale({ ...sale, tier_id: e.target.value })} data-testid={`sell-tier-${ev.id}`}
                        className="w-full bg-white/[0.06] border border-white/10 rounded-xl px-4 py-2.5 outline-none focus:border-[#2ec9c9]">
                        <option value="" className="text-[#16243b]">Ticket type…</option>
                        {(ev.ticket_tiers || []).map((t) => <option key={t.id} value={t.id} className="text-[#16243b]">{t.name} — £{Number(t.price).toFixed(2)}</option>)}
                      </select>
                      <div className="flex gap-2">
                        <input type="number" min="1" value={sale.quantity} onChange={(e) => setSale({ ...sale, quantity: e.target.value })} data-testid={`sell-qty-${ev.id}`}
                          placeholder="Qty" className="w-24 bg-white/[0.06] border border-white/10 rounded-xl px-4 py-2.5 text-white outline-none focus:border-[#2ec9c9]" />
                        <select value={sale.pay_method} onChange={(e) => setSale({ ...sale, pay_method: e.target.value })} data-testid={`sell-method-${ev.id}`}
                          className="flex-1 bg-white/[0.06] border border-white/10 rounded-xl px-4 py-2.5 outline-none focus:border-[#2ec9c9]">
                          <option value="cash" className="text-[#16243b]">Cash</option>
                          <option value="card" className="text-[#16243b]">Card</option>
                        </select>
                      </div>
                      <button disabled={busy} data-testid={`sell-submit-${ev.id}`} className="gp-btn-teal rounded-xl px-5 py-2.5 text-sm font-medium w-full disabled:opacity-60">Issue ticket(s)</button>
                    </form>
                  </div>
                )}
                {open === ev.id && mode[ev.id] === "guests" && (
                  <div className="p-4 border-t border-white/10 max-h-72 overflow-y-auto" data-testid={`guests-${ev.id}`}>
                    {guests.length === 0 ? <p className="text-white/50 text-sm">No tickets yet.</p> : guests.map((g) => (
                      <div key={g.code} className="flex items-center justify-between py-2 border-b border-white/5 text-sm">
                        <span>{g.attendee_name || "Guest"} <span className="text-white/40">· {g.tier_name}{g.seat ? ` · ${formatSeat(g.seat)}` : ""}</span></span>
                        {g.checked_in ? <span className="text-green-400 text-xs">Checked in</span> : <span className="text-white/40 text-xs">Not in</span>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="border-t border-white/10 p-4 flex items-center gap-2 text-sm text-white/50">
                <Lock size={14} /> Door access not enabled by the organiser. You can still flag this event if it looks suspicious.
              </div>
            )}
          </div>
        ))}
      </div>
      )}
    </div>
  );
}
